// src/hooks/useAnalyticsPing.js
//
// Sends a heartbeat to the backend every 2 minutes so the Manager Dashboard
// can display "Active Now" (users seen in the last 5 minutes).
// Safe to call once at the top of <App />.
//
import { useEffect } from 'react';

const PING_ENDPOINT = '/backend/api/analytics-ping';
const INTERVAL_MS = 2 * 60 * 1000; // 2 minutes

const getOrCreateSessionId = () => {
  try {
    let sid = localStorage.getItem('analytics_sid');
    if (!sid) {
      sid =
        's_' +
        Math.random().toString(36).slice(2, 10) +
        Date.now().toString(36);
      localStorage.setItem('analytics_sid', sid);
    }
    return sid;
  } catch {
    return 's_' + Date.now().toString(36);
  }
};

export default function useAnalyticsPing() {
  useEffect(() => {
    // Don't ping in prerender / non-browser contexts
    if (typeof window === 'undefined') return;

    const sid = getOrCreateSessionId();
    let cancelled = false;

    const ping = () => {
      if (cancelled) return;
      // Use fetch to avoid axios interceptors (no need for lang / auth header)
      try {
        const body = JSON.stringify({ session_id: sid });
        if (navigator.sendBeacon) {
          // Prefer sendBeacon — fire & forget, works on unload
          const blob = new Blob([body], { type: 'application/json' });
          navigator.sendBeacon(PING_ENDPOINT, blob);
        } else {
          fetch(PING_ENDPOINT, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body,
            keepalive: true,
          }).catch(() => {});
        }
      } catch (_) {
        /* noop */
      }
    };

    // Fire immediately on mount
    ping();

    // Then every 2 minutes
    const interval = setInterval(ping, INTERVAL_MS);

    // Also ping on tab focus (user returned to tab)
    const onVisibility = () => {
      if (document.visibilityState === 'visible') ping();
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      cancelled = true;
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);
}